import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { PageHeader } from "@/components/PageHeader";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { TrendingUp, AlertTriangle, Truck, Wrench } from "lucide-react";

const COLORS = {
  primary: "#0038FF",
  accent: "#FF5A00",
  success: "#00C853",
  warning: "#FFD600",
  danger: "#FF3D00",
  neutral: "#9CA3AF",
  dark: "#111827",
};

const STATUS_COLORS = {
  available: COLORS.success,
  leased: COLORS.warning,
  in_workshop: COLORS.primary,
  out_of_service: COLORS.danger,
};
const STATUS_LABEL = {
  available: "Disponible",
  leased: "Arrendado",
  in_workshop: "En Taller",
  out_of_service: "Fuera de Servicio",
};

export default function Insights() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/insights").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="p-8 text-gray-500">Cargando insights...</div>;

  const showOvercharge = user.role === "rentacar_admin" || user.role === "superadmin";

  return (
    <div data-testid="insights-page">
      <PageHeader
        tag="Inteligencia"
        title="Insights & Analítica"
        description="Visualiza el comportamiento de tu flota: tendencias mensuales, distribución de estados, intervalos de mantención y predicción de vencimientos."
      />

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-200 border border-gray-200 mb-10">
        <KpiTile label="Vehículos" value={data.summary.total_vehicles} icon={Truck} testId="insight-vehicles" />
        <KpiTile label="Órdenes (12m)" value={data.summary.total_orders_12m} icon={Wrench} testId="insight-orders" />
        {showOvercharge && (
          <>
            <KpiTile label="Sobrecargos detectados" value={data.overcharge_count} icon={AlertTriangle} accent="warning" testId="insight-overcharge-count" />
            <KpiTile label="Total sobrecargo CLP" value={`$${(data.overcharge_total_clp || 0).toLocaleString("es-CL")}`} icon={TrendingUp} accent="danger" testId="insight-overcharge-total" />
          </>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-px bg-gray-200 border border-gray-200">
        {/* Orders per month */}
        <ChartCard title="Órdenes por mes" subtitle="Últimos 12 meses · stacked por categoría">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.orders_per_month} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip contentStyle={{ border: "1px solid #000", borderRadius: 0, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="maintenance" stackId="a" fill={COLORS.primary} name="Mantención" />
              <Bar dataKey="repair" stackId="a" fill={COLORS.accent} name="Reparación" />
              <Bar dataKey="service" stackId="a" fill={COLORS.success} name="Servicio" />
              <Bar dataKey="others" stackId="a" fill={COLORS.neutral} name="Otros" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Status pie */}
        <ChartCard title="Distribución de estados" subtitle="Snapshot actual de la flota">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data.status_distribution}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={(e) => `${STATUS_LABEL[e.name] || e.name}: ${e.value}`}
                labelLine={false}
              >
                {data.status_distribution.map((entry) => (
                  <Cell key={entry.name} fill={STATUS_COLORS[entry.name] || COLORS.neutral} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ border: "1px solid #000", borderRadius: 0, fontSize: 12 }} formatter={(v, n) => [v, STATUS_LABEL[n] || n]} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Top vehicles */}
        <ChartCard title="Top 10 vehículos por # órdenes" subtitle="Identifica las unidades con mayor demanda de servicio">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.top_vehicles_by_orders} layout="vertical" margin={{ left: 50 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
              <YAxis type="category" dataKey="plate" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} width={70} />
              <Tooltip contentStyle={{ border: "1px solid #000", borderRadius: 0, fontSize: 12 }} />
              <Bar dataKey="orders" fill={COLORS.primary} name="Órdenes" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Avg interval */}
        <ChartCard title="Intervalo promedio de mantención" subtitle="Por vehículo (top 10) · días entre mantenciones">
          {data.interval_per_vehicle.length === 0 ? (
            <EmptyChart text="No hay suficientes mantenciones para calcular intervalos." />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.interval_per_vehicle}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="plate" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ border: "1px solid #000", borderRadius: 0, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="avg_days" fill={COLORS.accent} name="Días promedio" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* Expirations next 6 months */}
        <ChartCard title="Vencimientos por mes" subtitle="Próximos 6 meses · documentos por vencer" wide>
          {data.expirations_next_months.length === 0 ? (
            <EmptyChart text="Sin documentos próximos a vencer." />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={data.expirations_next_months}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={{ border: "1px solid #000", borderRadius: 0, fontSize: 12 }} />
                <Line type="monotone" dataKey="expirations" stroke={COLORS.danger} strokeWidth={3} dot={{ r: 5, fill: COLORS.danger }} name="Documentos por vencer" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>
    </div>
  );
}

function KpiTile({ label, value, icon: Icon, accent, testId }) {
  const bg = accent === "danger" ? "bg-red-50" : accent === "warning" ? "bg-yellow-50" : "bg-white";
  return (
    <div className={`${bg} p-6`} data-testid={testId}>
      <div className="flex justify-between items-start">
        <div>
          <p className="veta-tag">{label}</p>
          <p className="font-display text-3xl md:text-4xl tracking-tighter mt-2">{value}</p>
        </div>
        {Icon && <Icon size={22} strokeWidth={2.5} className="text-black" />}
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, wide, children }) {
  return (
    <div className={`bg-white p-6 ${wide ? "lg:col-span-2" : ""}`}>
      <p className="veta-tag">{subtitle}</p>
      <h3 className="font-display text-xl md:text-2xl tracking-tight mb-4">{title}</h3>
      {children}
    </div>
  );
}

function EmptyChart({ text }) {
  return <div className="h-[300px] flex items-center justify-center text-gray-400 text-sm border border-dashed border-gray-200">{text}</div>;
}
